import { type FC, useContext } from 'react';

import { useCompleteContractMutation } from '@/api/queries/contract';
import {
  getSpecContractModalToOpen,
  specContractRequirenments,
} from '@/common/config/contract';
import { useNextSpecContractTime } from '@/common/hooks/next-spec-contract-time';
import {
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import { SpecContractItem } from '@/components/Contract/SpecContractItem/SpecContractItem';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  selectSpecContractDataByKind,
  selectSpecContractIsFail,
} from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

import { ContractItemAnimationWrapper } from '../ContractItemAnimationWrapper/ContractItemAnimationWrapper';

interface NextContractInProps {
  kind: SpecContractType | PaidSpecContractType;
}

const NextContractIn: FC<NextContractInProps> = ({ kind }) => {
  const { openModal } = useContext(ModalContext);
  const contract = useAppSelector((state) =>
    selectSpecContractDataByKind(state, kind),
  );

  const timeToNextContract = useNextSpecContractTime(kind);
  const [, { isUninitialized }] = useCompleteContractMutation({
    fixedCacheKey: 'shared-complete-contract',
  });

  const hasContractFail = useAppSelector((state) =>
    selectSpecContractIsFail(state, kind),
  );

  const reqLevel = specContractRequirenments[kind].level;

  if (hasContractFail) {
    return (
      <ContractItemAnimationWrapper isUninitialized={isUninitialized}>
        <SpecContractItem
          kind={kind}
          timeLeft={timeToNextContract || 0}
          contractTimerExtensionDuration={null}
          status="fail"
          requairedLevel={reqLevel}
          onClick={() => {
            if (contract) {
              openModal(getSpecContractModalToOpen(kind, 'fail', contract));
            }
          }}
        />
      </ContractItemAnimationWrapper>
    );
  }

  return (
    !contract &&
    (timeToNextContract || 0) > 0 && (
      <ContractItemAnimationWrapper isUninitialized={isUninitialized}>
        <SpecContractItem
          kind={kind}
          timeLeft={timeToNextContract || 0}
          contractTimerExtensionDuration={null}
          status="wait"
          requairedLevel={reqLevel}
        />
      </ContractItemAnimationWrapper>
    )
  );
};

export { NextContractIn };
